import { QRCodeApiResponse } from "../types";

export const API_BASE_URL = "https://api.pingowner.com/api/v1";
export const GDRIVE_BASE_URL =
  "https://drive.google.com/uc?export=download&id=";
export interface ApiResponse<T = any> {
  error: boolean;
  message: string;
  statusCode: number;
  data?: T;
}

export interface ApiError {
  error: boolean;
  statusCode: number;
  message: string;
}

const TOKEN_EXPIRED_SUBSTR = "token has expired";

function isExpiredAccessTokenError(status: number, body: unknown): boolean {
  if (status !== 401 || !body || typeof body !== "object") return false;
  const o = body as Record<string, unknown>;
  return (
    o.error === true &&
    typeof o.message === "string" &&
    o.message.toLowerCase().includes(TOKEN_EXPIRED_SUBSTR)
  );
}

function readRefreshTokenFromStorage(): string | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw) as { refresh_token?: string };
    return u.refresh_token ?? null;
  } catch {
    return null;
  }
}

function persistSessionAfterRefresh(data: {
  id: string;
  email: string;
  admin_type: string;
  token: string;
  token_expiry: number;
  refresh_token: string;
}): void {
  localStorage.setItem("token", data.token);
  let merged = {
    id: data.id,
    email: data.email,
    admin_type: data.admin_type,
    token: data.token,
    token_expiry: data.token_expiry,
    refresh_token: data.refresh_token,
  };
  const raw = localStorage.getItem("user");
  if (raw) {
    try {
      merged = { ...JSON.parse(raw), ...merged };
    } catch {
      // keep merged from refresh payload only
    }
  }
  localStorage.setItem("user", JSON.stringify(merged));
  window.dispatchEvent(new CustomEvent("auth:tokens-refreshed"));
}

function clearSessionAndNotify(): void {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  window.dispatchEvent(new CustomEvent("auth:session-expired"));
}

class ApiService {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private endpointSkipsAuthHeader(endpoint: string): boolean {
    return (
      endpoint.includes("/cms/admin/login") ||
      endpoint.includes("/cms/admin/refresh-token")
    );
  }

  private async refreshSession(): Promise<boolean> {
    const refreshToken = readRefreshTokenFromStorage();
    if (!refreshToken) {
      clearSessionAndNotify();
      return false;
    }

    const res = await fetch(`${this.baseUrl}/cms/admin/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    let data: ApiResponse | null = null;
    try {
      data = await res.json();
    } catch {
      clearSessionAndNotify();
      return false;
    }

    if (
      !data ||
      !res.ok ||
      data.error ||
      !data.data?.token ||
      !data.data?.refresh_token
    ) {
      clearSessionAndNotify();
      return false;
    }

    const d = data.data as {
      id: string;
      email: string;
      admin_type: string;
      token: string;
      token_expiry: number;
      refresh_token: string;
    };
    persistSessionAfterRefresh(d);
    return true;
  }

  private async ensureRefreshedSession(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshSession().finally(() => {
        this.refreshPromise = null;
      });
    }
    const ok = await this.refreshPromise;
    if (!ok) {
      throw new Error("Your session has expired. Please log in again.");
    }
  }

  /**
   * Authenticated fetch with one automatic retry after refresh when the API
   * returns 401 for an expired access token.
   */
  private async fetchWithAuth(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem("token");
    const headers = new Headers(options.headers);

    // Always set JSON content type for string bodies (including login/refresh paths).
    // Skipping this for /login caused the server to miss the body → "Required fields are missing."
    if (
      !headers.has("Content-Type") &&
      options.body &&
      typeof options.body === "string"
    ) {
      headers.set("Content-Type", "application/json");
    }

    if (token && !this.endpointSkipsAuthHeader(endpoint)) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(url, { ...options, headers });

    if (
      response.status === 401 &&
      !isRetry &&
      !this.endpointSkipsAuthHeader(endpoint)
    ) {
      let body: unknown;
      try {
        body = await response.clone().json();
      } catch {
        body = null;
      }
      if (isExpiredAccessTokenError(401, body)) {
        await this.ensureRefreshedSession();
        return this.fetchWithAuth(endpoint, options, true);
      }
    }

    return response;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const response = await this.fetchWithAuth(endpoint, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          (data as ApiResponse)?.message ||
            `HTTP error! status: ${response.status}`
        );
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  /** Same auth + refresh-on-401 behavior; use when the body is not JSON (e.g. blob). */
  authorizedFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    return this.fetchWithAuth(endpoint, options);
  }
}

export const apiService = new ApiService(API_BASE_URL);

export const marketingApi = {
  getVisitors: async (params?: any) => {
    const q = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) q.append(k, String(v));
      });
    }
    return apiService.get<any>(`/cms/admin/visitors?${q.toString()}`);
  },
  getAttributionReport: async (params?: any) => {
    const q = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) q.append(k, String(v));
      });
    }
    return apiService.get<any>(`/cms/admin/attribution-report?${q.toString()}`);
  },
};

// Auth-specific API functions
export const authApi = {
  login: async (email: string, password: string) => {
    return apiService.post<ApiResponse>("/cms/admin/login", {
      email,
      password,
    });
  },
};

export const dashboardApi = {
  getDashboardCounts: () =>
    apiService.get<ApiResponse>("/cms/admin/dashboard-counts"),
  getRecentScans: () => apiService.get<ApiResponse>("/cms/admin/recent-scans"),
};

// QR Codes API functions
export const qrCodesApi = {
  getAllQRCodes: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    sort = "-batch_id"
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search,
      sort: sort,
    });
    return apiService.get<QRCodeApiResponse>(
      `/cms/admin/get-all-qr-codes?${params}`
    );
  },
  updateQrCodeActiveStatus: async (id: string, isActive?: boolean) => {
    return apiService.post<ApiResponse>(`/cms/admin/update-qr-details`, {
      isActive,
      id,
    });
  },
  updateQrCodeUsedStatus: async (id: string, status?: string) => {
    return apiService.post<ApiResponse>(`/cms/admin/update-qr-details`, {
      status,
      id,
    });
  },
  getQrCodeById: async (id: string) => {
    return apiService.get<ApiResponse>(
      `/cms/admin/get-document-by-id-and-type?type=qrcode&id=${id}`
    );
  },
  generateQrCode: async (data: any) => {
    return apiService.post<ApiResponse>("/cms/admin/generate-qr-code", data);
  },
  previewQrCode: async (data: any) => {
    const response = await apiService.authorizedFetch(
      "/cms/admin/preview-qr-code",
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData?.message) message = errorData.message;
      } catch {
        // ignore JSON parse errors for non-JSON responses
      }
      throw new Error(message);
    }

    return response.blob();
  },
};

// Scans API functions
export const scansApi = {
  getAllScans: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    startDate?: string,
    endDate?: string
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search || "",
    });

    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return apiService.get<any>(`/cms/admin/get-all-scans?${params}`);
  },
  getScanDataByDateAndQrId: async ({
    startDate,
    endDate,
    qr_code_id,
  }: {
    startDate: string;
    endDate: string;
    qr_code_id?: string;
  }) => {
    const body: Record<string, any> = {
      startDate,
      endDate,
    };
    if (qr_code_id) {
      body.qr_code_id = qr_code_id;
    }
    return apiService.post<any>(
      "/cms/admin/get-scan-data-by-date-and-qr-id",
      body
    );
  },
};

// Users API functions
export const usersApi = {
  getAllUsers: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    filters?: Record<string, string>
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search || "",
    });

    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
    }

    return apiService.get<any>(`/cms/admin/get-all-users?${params}`);
  },
  getUserById: async (id: string) => {
    return apiService.get<ApiResponse>(
      `/cms/admin/get-document-by-id-and-type?type=user&id=${id}`
    );
  },
  updateUserDetails: async (data: any) => {
    return apiService.post<ApiResponse>(`/cms/admin/edit-user-details`, data);
  },
  deleteUser: async (data: any) => {
    return apiService.post<ApiResponse>(`/cms/admin/delete-user`, data);
  },
};

// Admin feedbacks
export const feedbacksApi = {
  getFeedbacks: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    options?: {
      isVisible?: boolean;
      startDate?: string;
      endDate?: string;
    }
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search || "",
    });
    if (options?.isVisible === true) {
      params.append("isVisible", "true");
    }
    if (options?.isVisible === false) {
      params.append("isVisible", "false");
    }
    if (options?.startDate) {
      params.append("startDate", options.startDate);
    }
    if (options?.endDate) {
      params.append("endDate", options.endDate);
    }
    return apiService.get<any>(`/cms/admin/feedbacks?${params}`);
  },

  patchVisibility: async (id: string, isVisible: boolean) => {
    return apiService.patch<ApiResponse>(
      `/cms/admin/feedbacks/${id}/visibility`,
      { isVisible }
    );
  },
};

export const supportTicketsApi = {
  getAllSupportTickets: async () => {
    return apiService.get<ApiResponse>("/cms/admin/get-all-support-tickets");
  },

  updateSupportTicketStatus: async (id: string, status: string) => {
    return apiService.post<ApiResponse>(
      "/cms/admin/update-support-ticket-status",
      { id, status }
    );
  },
};

export const stickerPackagesApi = {
  getAllStickerPackages: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    isActive?: boolean
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search || "",
    });

    if (typeof isActive === "boolean") {
      params.append("isActive", String(isActive));
    }

    return apiService.get<ApiResponse>(`/cms/admin/sticker-packages?${params}`);
  },
  createStickerPackage: async (data: any) => {
    return apiService.post<ApiResponse>(`/cms/admin/sticker-packages`, data);
  },
  updateStickerPackage: async (id: string, data: any) => {
    return apiService.patch<ApiResponse>(
      `/cms/admin/sticker-packages/${id}`,
      data
    );
  },
  deleteStickerPackage: async (id: string) => {
    return apiService.delete<ApiResponse>(`/cms/admin/sticker-packages/${id}`);
  },
};

export const ordersApi = {
  getPaymentOrders: async (
    page: number = 1,
    limit: number = 10,
    filters?: {
      payment_status?: string;
      app_source?: string;
      app_id?: string;
      search?: string;
      from_date?: string;
      to_date?: string;
      min_amount?: string;
      max_amount?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
      source?: string;
      medium?: string;
      campaign?: string;
      country?: string;
      state?: string;
      city?: string;
    }
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.payment_status && filters.payment_status !== "all") {
      params.append("payment_status", filters.payment_status);
    }
    if (filters?.app_source) {
      params.append("app_source", filters.app_source);
    }
    if (filters?.app_id) {
      params.append("app_id", filters.app_id);
    }
    if (filters?.search) {
      params.append("search", filters.search);
    }
    if (filters?.from_date) {
      params.append("from_date", filters.from_date);
    }
    if (filters?.to_date) {
      params.append("to_date", filters.to_date);
    }
    if (filters?.min_amount) {
      params.append("min_amount", filters.min_amount);
    }
    if (filters?.max_amount) {
      params.append("max_amount", filters.max_amount);
    }
    if (filters?.sort_by) {
      params.append("sort_by", filters.sort_by);
    }
    if (filters?.sort_order) {
      params.append("sort_order", filters.sort_order);
    }
    if (filters?.source) params.append("source", filters.source);
    if (filters?.medium) params.append("medium", filters.medium);
    if (filters?.campaign) params.append("campaign", filters.campaign);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.state) params.append("state", filters.state);
    if (filters?.city) params.append("city", filters.city);

    return apiService.get<any>(`/cms/admin/payment-orders?${params}`);
  },
  getPaymentInsights: async (filters?: {
    from_date?: string;
    to_date?: string;
    app_source?: string;
    app_id?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.from_date) {
      params.append("from_date", filters.from_date);
    }
    if (filters?.to_date) {
      params.append("to_date", filters.to_date);
    }
    if (filters?.app_source) {
      params.append("app_source", filters.app_source);
    }
    if (filters?.app_id) {
      params.append("app_id", filters.app_id);
    }

    return apiService.get<any>(`/cms/admin/payment-insights?${params}`);
  },
};

export const newslettersApi = {
  getNewsletterById: async (id: string) => {
    return apiService.get<any>(`/cms/newsletters/${id}`);
  },
  getNewsletterStats: async (id: string) => {
    return apiService.get<ApiResponse<{
      total: number;
      sent: number;
      failed: number;
      opened: number;
      clicked: number;
    }>>(`/cms/newsletters/${id}/stats`);
  },
  getNewsletters: async (
    page: number = 1,
    limit: number = 10,
    search: string = ""
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search || "",
    });
    return apiService.get<any>(`/cms/newsletters?${params}`);
  },
  createNewsletter: async (data: { title: string; subject: string; content: string }) => {
    return apiService.post<any>(`/cms/newsletters`, data);
  },
  sendNewsletter: async (id: string) => {
    return apiService.post<any>(`/cms/newsletters/${id}/send`);
  },
  deleteNewsletter: async (id: string) => {
    return apiService.delete<any>(`/cms/newsletters/${id}`);
  },
};

export const couponsApi = {
  listCoupons: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    isActive?: boolean,
    type?: string
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.append("search", search);
    if (typeof isActive === "boolean") params.append("isActive", String(isActive));
    if (type && type !== "all") params.append("type", type);
    return apiService.get<ApiResponse>(`/admin/coupons?${params}`);
  },
  getCoupon: async (id: string) => {
    return apiService.get<ApiResponse>(`/admin/coupons/${id}`);
  },
  createCoupon: async (data: any) => {
    return apiService.post<ApiResponse>(`/admin/coupons`, data);
  },
  updateCoupon: async (id: string, data: any) => {
    return apiService.put<ApiResponse>(`/admin/coupons/${id}`, data);
  },
  disableCoupon: async (id: string) => {
    return apiService.patch<ApiResponse>(`/admin/coupons/${id}/disable`);
  },
  getCouponUsage: async (id: string, page: number = 1, limit: number = 10) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    return apiService.get<ApiResponse>(`/admin/coupons/${id}/usage?${params}`);
  },
};

export const subscribersApi = {
  getSubscribers: async (
    page: number = 1,
    limit: number = 10,
    search: string = ""
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search || "",
    });

    return apiService.get<any>(`/cms/subscribers?${params}`);
  },
  updateSubscriberActiveStatus: async (
    id: string,
    status: "activated" | "deactivated"
  ) => {
    return apiService.patch<any>(`/cms/subscribers/${id}`, {
      status,
    });
  },
};
